import { AIEditBlockConfig, AIEditBlockResponse, AIEditOptions } from "../ai-block-types";
import { generateAIBlock } from "frontend/api";

export interface EditBlockConfig {
  /** Converts response items to element patches. Must handle the mapping properly. */
  responseToPatch: (item: string, index: number) => Record<string, unknown>;
  /** Optional: additional parameters to pass to backend (e.g., tone, targetLanguage) */
  additionalParams?: (options?: AIEditOptions) => Record<string, unknown>;
}

/**
 * Factory function to create an edit block with proper element-to-patch mapping.
 *
 * Each block must explicitly define how response items map to element patches.
 * The LLM receives a numbered list [1] item1, [2] item2, etc. and returns
 * responses in the same order. Position in array = element index.
 *
 * @param name - The block name (must match backend block name)
 * @param config - Block configuration with responseToPatch function
 */
export function createEditBlock(name: string, config: EditBlockConfig): AIEditBlockConfig {
  return {
    name,
    blockType: "edit",
    apiCall: async (prompt: string, blockOptions?: AIEditOptions) => {
      const texts = blockOptions?.texts || [];
      const additionalParams = config.additionalParams?.(blockOptions) || {};

      return generateAIBlock(name, prompt, undefined, undefined, undefined, {
        texts,
        elementIds: blockOptions?.elementIds || [],
        ...additionalParams,
      });
    },
    responseToEdits: async (response: unknown, blockOptions?: AIEditOptions) => {
      const parsed = response as { edits: string[] };
      const items = parsed.edits || [];
      const elementIds = blockOptions?.elementIds || [];

      const edits: AIEditBlockResponse[] = items.map((item, index) => {
        const elementId = elementIds[index];
        if (!elementId) {
          throw new Error(`No element ID found for index ${index}. Ensure elementIds array matches response length.`);
        }

        return {
          elementId,
          patch: config.responseToPatch(item, index),
        };
      });

      return { edits };
    },
  };
}
